import React, { useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled, { keyframes } from 'styled-components'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded'
import SearchableSelect from 'components/SearchableSelect'
import { SkeletonLine } from 'components/Skeleton'
import { ACCEPT_ATTRIBUTE, ACTIVE_STATUSES, questionCountText } from './formGeneration'

export const stepFor = ({ generation, timedOut }) => {
  if (!generation) return 'choose'
  if (generation.status === 'ready') return 'ready'
  if (generation.status === 'failed') return 'failed'
  if (timedOut) return 'timeout'
  if (ACTIVE_STATUSES.includes(generation.status)) return 'progress'
  return 'progress'
}

const GenerateFormModal = ({
  generation,
  uploading,
  uploadError,
  timedOut,
  organisationOptions = [],
  organisationsLoading = false,
  needsOrganisation = false,
  organisationId,
  onOrganisationChange,
  onFile,
  onOpenDraft,
  onTryAgain,
  onClose
}) => {
  const titleId = useId()
  const inputRef = useRef(null)
  const [dragging, setDragging] = useState(false)
  const step = stepFor({ generation, timedOut })
  const canChoose = !uploading && !organisationsLoading && (!needsOrganisation || !!organisationId)
  const sharedWith = organisationOptions.length === 1 ? organisationOptions[0].name : null

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const pickFile = files => {
    const file = files && files[0]
    if (file) onFile(file)
  }

  const titles = {
    choose: 'Create a form from your checklist',
    progress: 'Reading your checklist',
    timeout: 'This is taking longer than usual',
    ready: 'Your draft form is ready',
    failed: 'We could not turn this file into a form'
  }

  const node = (
    <Overlay onMouseDown={onClose}>
      <Card role='dialog' aria-modal='true' aria-labelledby={titleId} onMouseDown={e => e.stopPropagation()} data-testid='form-gen-modal'>
        <Head>
          <Title id={titleId}>{titles[step]}</Title>
          <IconClose type='button' aria-label='Close' onClick={onClose}><CloseRoundedIcon /></IconClose>
        </Head>

        {step === 'choose' && (
          <>
            <Lead>We will read your checklist and turn it into a form. You can check and change every question before anyone uses it.</Lead>

            {organisationsLoading && <SkeletonLine $w='100%' $h={44} />}
            {!organisationsLoading && needsOrganisation && (
              <Field>
                <FieldLabel>Which organisation is this form for?</FieldLabel>
                <SearchableSelect
                  value={organisationId || ''}
                  onChange={onOrganisationChange}
                  options={organisationOptions.map(o => ({ value: o.id, label: o.name }))}
                  placeholder='Choose an organisation'
                  searchPlaceholder='Search organisations…'
                  ariaLabel='Organisation for this form'
                />
                <FieldHint>Everyone in the organisation you choose will be able to use the form once you publish it.</FieldHint>
              </Field>
            )}

            <Drop
              $dragging={dragging}
              $disabled={!canChoose}
              onDragOver={e => { e.preventDefault(); if (canChoose) setDragging(true) }}
              onDragLeave={() => setDragging(false)}
              onDrop={e => {
                e.preventDefault()
                setDragging(false)
                if (canChoose) pickFile(e.dataTransfer?.files)
              }}
            >
              <UploadFileRoundedIcon aria-hidden='true' />
              <DropText>Drag your file here, or</DropText>
              <PrimaryBtn
                type='button'
                onClick={() => inputRef.current && inputRef.current.click()}
                disabled={!canChoose}
                data-tour-id='formGen.upload'
              >
                {uploading ? 'Uploading…' : 'Choose a file'}
              </PrimaryBtn>
              <Types>PDF, Word, Excel or a photo of your paper checklist, up to 10 MB</Types>
              <HiddenInput
                ref={inputRef}
                type='file'
                accept={ACCEPT_ATTRIBUTE}
                aria-label='Checklist file'
                data-testid='form-gen-file'
                onChange={e => { pickFile(e.target.files); e.target.value = '' }}
              />
            </Drop>
            {!organisationsLoading && needsOrganisation && !organisationId && <FieldHint>Choose an organisation first, then choose your file.</FieldHint>}
            {sharedWith && <FieldHint>Once you publish it, everyone in {sharedWith === 'Your organisation' ? 'your organisation' : sharedWith} will be able to use this form.</FieldHint>}

            <Note>
              <InfoOutlinedIcon aria-hidden='true' />
              <span>Do not upload documents with personal details such as passports or ID.</span>
            </Note>

            {uploadError && <ErrorText role='alert'>{uploadError}</ErrorText>}
          </>
        )}

        {step === 'progress' && (
          <>
            <Status role='status' aria-live='polite'>{generation.stageLabel || 'Waiting to start…'}</Status>
            <Track aria-hidden='true'><Bar /></Track>
            {generation.sourceFilename && <Muted>File: {generation.sourceFilename}</Muted>}
            <Lead>You can close this window. We will keep working and let you know when your draft is ready.</Lead>
            <Foot>
              <SecondaryBtn type='button' onClick={onClose}>Close</SecondaryBtn>
            </Foot>
          </>
        )}

        {step === 'timeout' && (
          <>
            <StateRow>
              <ScheduleRoundedIcon aria-hidden='true' />
              <span>Your draft may still appear in Form templates in a few minutes. You can also try another file.</span>
            </StateRow>
            <Foot>
              <SecondaryBtn type='button' onClick={onClose}>Close</SecondaryBtn>
              <PrimaryBtn type='button' onClick={onTryAgain}>Try another file</PrimaryBtn>
            </Foot>
          </>
        )}

        {step === 'ready' && (
          <>
            <StateRow $tone='ready' role='status'>
              <TaskAltRoundedIcon aria-hidden='true' />
              <span>{generation.formTemplateName || 'Untitled form'}, {questionCountText(generation.questionCount)}</span>
            </StateRow>
            <Lead>Nobody can use it until you check it and publish it.</Lead>
            <Foot>
              <SecondaryBtn type='button' onClick={onClose}>Close</SecondaryBtn>
              <PrimaryBtn type='button' onClick={onOpenDraft} data-tour-id='formGen.ready'>Check and publish</PrimaryBtn>
            </Foot>
          </>
        )}

        {step === 'failed' && (
          <>
            <StateRow $tone='failed' role='alert'>
              <ErrorOutlineRoundedIcon aria-hidden='true' />
              <span>{generation.error || 'Something went wrong while reading this file.'}</span>
            </StateRow>
            <Foot>
              <SecondaryBtn type='button' onClick={onClose}>Close</SecondaryBtn>
              <PrimaryBtn type='button' onClick={onTryAgain}>Try another file</PrimaryBtn>
            </Foot>
          </>
        )}
      </Card>
    </Overlay>
  )

  return createPortal(node, document.body)
}

export default GenerateFormModal

const fade = keyframes`from { opacity: 0 } to { opacity: 1 }`
const slide = keyframes`from { transform: translateX(-100%) } to { transform: translateX(250%) }`

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1400;
  background: rgba(12, 18, 41, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  box-sizing: border-box;
  animation: ${fade} 0.15s ease;
`

const Card = styled.div`
  width: 520px;
  max-width: 100%;
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  box-sizing: border-box;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 12px 32px rgba(12, 18, 41, 0.16);
  padding: 20px 22px 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`

const Title = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
  color: #0C1229;
`

const IconClose = styled.button`
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #475569;
  cursor: pointer;
  &:hover { background: #f1f5f9; }
  &:focus-visible { outline: 2px solid #33becc; outline-offset: 2px; }
`

const Lead = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.5;
  color: #334155;
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const FieldLabel = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: #0C1229;
`

const FieldHint = styled.span`
  font-size: 14px;
  color: #475569;
  line-height: 1.4;
`

const Drop = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 22px 16px;
  border-radius: 12px;
  border: 2px dashed ${({ $dragging }) => ($dragging ? '#33becc' : '#cbd5e1')};
  background: ${({ $dragging }) => ($dragging ? '#eaf8fa' : '#f8fafc')};
  text-align: center;
  opacity: ${({ $disabled }) => ($disabled ? 0.7 : 1)};

  > svg { font-size: 32px; color: #1f8b98; }
`

const DropText = styled.span`
  font-size: 15px;
  color: #334155;
`

const Types = styled.span`
  font-size: 14px;
  color: #475569;
`

const HiddenInput = styled.input`
  display: none;
`

const Note = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  padding: 10px 12px;
  border-radius: 10px;
  background: #f1f5f9;
  color: #334155;
  font-size: 14px;
  line-height: 1.45;

  svg { font-size: 18px; color: #475569; flex: 0 0 auto; margin-top: 1px; }
`

const ErrorText = styled.div`
  padding: 10px 12px;
  border-radius: 10px;
  background: #fdecea;
  color: #b00020;
  font-size: 15px;
  line-height: 1.45;
`

const Status = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #0C1229;
`

const Track = styled.div`
  position: relative;
  height: 6px;
  border-radius: 999px;
  background: #e2e8f0;
  overflow: hidden;
`

const Bar = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 40%;
  height: 100%;
  border-radius: 999px;
  background: #33becc;
  animation: ${slide} 1.6s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    width: 100%;
    opacity: 0.5;
  }
`

const Muted = styled.span`
  font-size: 14px;
  color: #475569;
  overflow-wrap: anywhere;
`

const StateRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 10px;
  background: ${({ $tone }) => ($tone === 'failed' ? '#fdecea' : $tone === 'ready' ? '#eaf8fa' : '#f8fafc')};
  color: ${({ $tone }) => ($tone === 'failed' ? '#b00020' : '#0C1229')};
  font-size: 15px;
  font-weight: 600;
  line-height: 1.45;
  overflow-wrap: anywhere;

  svg { font-size: 22px; flex: 0 0 auto; color: ${({ $tone }) => ($tone === 'failed' ? '#b00020' : '#1f8b98')}; }
`

const Foot = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
`

const btn = `
  min-height: 44px;
  padding: 10px 18px;
  border-radius: 10px;
  font: inherit;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  &:focus-visible { outline: 2px solid #33becc; outline-offset: 2px; }
  &:disabled { cursor: not-allowed; opacity: 0.6; }
`

const PrimaryBtn = styled.button`
  ${btn}
  border: 1px solid #33becc;
  background: #33becc;
  color: #fff;
  &:hover:not(:disabled) { background: #2aa6b3; border-color: #2aa6b3; }
`

const SecondaryBtn = styled.button`
  ${btn}
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #0C1229;
  &:hover { background: #f1f5f9; }
`
